/**
 * @file Decorative macOS-window chrome for in-page terminal-styled UI.
 * @module @labcraft/www/ui/terminal-frame
 *
 * Pure presentational shell: traffic-light dots on the left, a
 * centered title bar, a spacer on the right for visual balance, and
 * a dark body that hosts arbitrary children.
 *
 * Distinct from `<TerminalView/>` (the xterm.js wrapper). This one is
 * static decoration — there's no pty behind it. Used by the contact
 * page to present the form as if you're typing into a shell, and
 * available for future docs/onboarding moments where the same visual
 * affordance helps.
 *
 * Body styling (padding, font, content layout) is the consumer's
 * responsibility — children render directly inside the frame.
 */

import type { JSX, ReactNode } from 'react'
import styles from './terminal_frame.module.css'

/**
 * Props for {@link TerminalFrame}.
 *
 * @property title    Centered text in the chrome bar (e.g. `labcraft.dev:~/contact`).
 * @property children Rendered inside the dark body, below the chrome bar.
 */
export interface TerminalFrameProps {
  title: string
  children: ReactNode
}

export function TerminalFrame(props: TerminalFrameProps): JSX.Element {
  return (
    <div className={styles.frame}>
      <div className={styles.chrome}>
        <div className={styles.dots} aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <div className={styles.title}>{props.title}</div>
        <div className={styles.spacer} aria-hidden />
      </div>
      {props.children}
    </div>
  )
}
